import type {
  AiCostsByModelResponse,
  ImageModelsRefreshResponse,
  ImageModelsResponse,
  SaveAlphaSettingsBody,
  SaveImageModelsBody,
} from "@nlc/shared-types";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { aiCostsKeys } from "@/hooks/queryKeys";

import type { AiCostsRange } from "../aiCostsSearch";

import { getAiCostsByModel } from "../api/aiCosts.api";
import {
  getImageModels,
  refreshImageModels,
  saveAlphaSettings,
  saveImageModels,
} from "../api/imageModels.api";

const imageModelsKey = [...aiCostsKeys.all, "image-models"] as const;

export function useImageModels(): UseQueryResult<ImageModelsResponse, Error> {
  return useQuery({ queryFn: getImageModels, queryKey: imageModelsKey });
}

export function useImageModelCosts(
  range: AiCostsRange,
): UseQueryResult<AiCostsByModelResponse, Error> {
  return useQuery({
    queryFn: () => getAiCostsByModel(range),
    queryKey: [
      ...imageModelsKey,
      "costs",
      range.from.toISOString(),
      range.to.toISOString(),
    ],
  });
}

function useSaveToModels<TBody>(
  save: (body: TBody) => Promise<ImageModelsResponse>,
): UseMutationResult<ImageModelsResponse, Error, TBody> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: save,
    onSuccess: (response) => {
      queryClient.setQueryData(imageModelsKey, response);
      void queryClient.invalidateQueries({ queryKey: imageModelsKey });
    },
  });
}

export function useSaveImageModels(): UseMutationResult<
  ImageModelsResponse,
  Error,
  SaveImageModelsBody
> {
  return useSaveToModels<SaveImageModelsBody>(saveImageModels);
}

export function useSaveAlphaSettings(): UseMutationResult<
  ImageModelsResponse,
  Error,
  SaveAlphaSettingsBody
> {
  return useSaveToModels<SaveAlphaSettingsBody>(saveAlphaSettings);
}

export function useRefreshImageModels(): UseMutationResult<
  ImageModelsRefreshResponse,
  Error,
  void
> {
  return useMutation({ mutationFn: refreshImageModels });
}
